import React, { useCallback, useEffect, useState } from "react";
import { PlatformStateProvider, setEntityOpener, setRuntimeConfig } from "nr1";
import { isCognitoConfigured, restoreSession, signOut } from "./auth/cognito";
import EntityPanel, { openEntityPanel } from "./entity/EntityPanel";
import LandingPage from "./LandingPage";
import HomeNerdlet from "./nerdlets/home/index.js";

/**
 * The chrome the New Relic platform normally provides: identity, account and
 * time context. Only the standalone build renders this — inside a nerdpack the
 * platform mounts HomeNerdlet directly, already authenticated.
 *
 * The dashboard's primary data source is a public HTTPS API, so it renders as
 * soon as the viewer is signed in; the NRQL-backed panels surface their own
 * error if no NerdGraph proxy is configured (see public/config.json).
 */
export default function StandaloneShell({ config }) {
  const gated = config.requireAuth !== false;

  const [session, setSession] = useState(null);
  // Avoids flashing the landing page while a stored session is being restored.
  const [restoring, setRestoring] = useState(gated);

  useEffect(() => {
    if (!gated) return undefined;

    let cancelled = false;
    restoreSession()
      .then((restored) => {
        if (!cancelled) setSession(restored);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gated]);

  // Route the platform's stacked-entity action into the standalone panel.
  useEffect(() => {
    setEntityOpener(openEntityPanel);
    return () => setEntityOpener(null);
  }, []);

  // Let the signed-in identity stand in for the New Relic user that UserQuery
  // resolves inside the platform.
  const handleAuthenticated = useCallback((next) => {
    setRuntimeConfig({ userEmail: next.email, userName: next.username });
    setSession(next);
  }, []);

  const handleSignOut = useCallback(() => {
    signOut();
    setRuntimeConfig({ userEmail: null, userName: null });
    setSession(null);
  }, []);

  if (gated && restoring) {
    return <div className="landing landing--restoring" />;
  }

  if (gated && !session) {
    return <LandingPage onAuthenticated={handleAuthenticated} />;
  }

  if (!gated && isCognitoConfigured()) {
    console.warn(
      "[health-dashboard] requireAuth is false — the dashboard is reachable without signing in."
    );
  }

  return (
    <ErrorBoundary>
      <PlatformStateProvider
        value={{ accountId: config.accountId, timeRange: config.timeRange }}
      >
        <HomeNerdlet />
        <EntityPanel />
        {session ? (
          <div className="session-bar">
            <span className="session-bar__user" title={session.email || session.username}>
              {session.email || session.username}
            </span>
            <button
              type="button"
              className="session-bar__signout"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </PlatformStateProvider>
    </ErrorBoundary>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[health-dashboard] render failed:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="nr1c-fatal">
          <h1>Something went wrong</h1>
          <pre>{String(this.state.error?.stack || this.state.error)}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}
